import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import permissionsRequests from "../../api/PermissionRequests";
import roleRequests from "../../api/RoleRequests";
import Loader from "../../components/utils/Loader";
import Navbar from "../../components/dashboard/Navbar";
import {
  Shield,
  Users,
  ArrowLeft,
  Save,
  Check,
  Search,
  AlertCircle,
  Info,
  ChevronRight,
} from "lucide-react";
import backupPermissions from "../../data/BackupPermissions";

const AddRole = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState(new Set());
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [roleData, setRoleData] = useState({
    name: "",
  });

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const response = await permissionsRequests.getPermissions();
      if (response.data.status_code === "000") {
        const apiPermissions = response.data.data;
        if (apiPermissions && Object.keys(apiPermissions).length > 0) {
          const flattenedPermissions = [];
          let idCounter = 1;

          Object.entries(apiPermissions).forEach(
            ([moduleName, permissions]) => {
              permissions.forEach((permission) => {
                let dependencies = [];
                if (
                  permission.name === "Create_Admin" ||
                  permission.name === "Edit_Admin" ||
                  permission.name === "Delete_Admin"
                ) {
                  dependencies = ["View_Admins"];
                } else if (
                  permission.name === "Create_Client" ||
                  permission.name === "Edit_Client" ||
                  permission.name === "Delete_Client" ||
                  permission.name === "Manage_Client_Status"
                ) {
                  dependencies = ["View_Clients"];
                } else if (permission.name === "Manage_Sender_Ids") {
                  dependencies = ["View_Sender_Ids"];
                } else if (permission.name === "Manage_Roles") {
                  dependencies = ["View_Roles"];
                } else if (permission.name === "Manage_Permissions") {
                  dependencies = ["View_Permissions"];
                } else if (permission.name === "Manage_Payments") {
                  dependencies = ["View_Payment_History", "View_Subscriptions"];
                }

                flattenedPermissions.push({
                  id: idCounter++,
                  name: permission.name,
                  description: `Permission to ${permission.name
                    .toLowerCase()
                    .replace(/_/g, " ")}`,
                  type: moduleName.replace(/_/g, " "),
                  permission_slug: permission.permission_slug,
                  dependencies: dependencies,
                });
              });
            }
          );

          setPermissions(flattenedPermissions);
        } else {
          setPermissions(backupPermissions);
          toast.warning("Using backup permissions. API returned empty data.");
        }
      } else {
        setPermissions(backupPermissions);
        toast.warning("Using backup permissions. API returned empty data.");
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      setPermissions(backupPermissions);
      toast.warning("Using backup permissions due to API error.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  const groupedPermissions = (permissions || []).reduce(
    (groups, permission) => {
      const type = permission.type || "Other";
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(permission);
      return groups;
    },
    {}
  );

  const filteredGroupedPermissions = Object.keys(groupedPermissions).reduce(
    (filtered, groupName) => {
      const groupPermissions = groupedPermissions[groupName].filter(
        (permission) =>
          permission.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          permission.description
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
      );

      if (groupPermissions.length > 0) {
        filtered[groupName] = groupPermissions;
      }
      return filtered;
    },
    {}
  );

  const togglePermission = (permission) => {
    const newSelected = new Set(selectedPermissions);

    if (newSelected.has(permission.id)) {
      newSelected.delete(permission.id);

      const dependentPermissions = (permissions || []).filter(
        (p) => p.dependencies && p.dependencies.includes(permission.name)
      );
      dependentPermissions.forEach((dep) => newSelected.delete(dep.id));
    } else {
      newSelected.add(permission.id);

      if (permission.dependencies) {
        permission.dependencies.forEach((depName) => {
          const depPermission = (permissions || []).find(
            (p) => p.name === depName
          );
          if (depPermission) {
            newSelected.add(depPermission.id);
          }
        });
      }
    }

    setSelectedPermissions(newSelected);
  };

  const toggleGroup = (groupName) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupName)) {
      newExpanded.delete(groupName);
    } else {
      newExpanded.add(groupName);
    }
    setExpandedGroups(newExpanded);
  };

  const selectAllInGroup = (groupName) => {
    const groupPermissions = groupedPermissions[groupName];
    const newSelected = new Set(selectedPermissions);

    groupPermissions.forEach((permission) => {
      newSelected.add(permission.id);
      if (permission.dependencies) {
        permission.dependencies.forEach((depName) => {
          const depPermission = (permissions || []).find(
            (p) => p.name === depName
          );
          if (depPermission) {
            newSelected.add(depPermission.id);
          }
        });
      }
    });

    setSelectedPermissions(newSelected);
  };

  const clearAllInGroup = (groupName) => {
    const groupPermissions = groupedPermissions[groupName];
    const newSelected = new Set(selectedPermissions);

    groupPermissions.forEach((permission) => {
      newSelected.delete(permission.id);
    });

    setSelectedPermissions(newSelected);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!roleData.name.trim()) {
      toast.error("Role name is required");
      return;
    }

    if (selectedPermissions.size === 0) {
      toast.error("Please select at least one permission");
      return;
    }

    setSubmitting(true);
    try {
      const selectedPermissionSlugs = Array.from(selectedPermissions).map(
        (id) => {
          const permission = permissions.find((p) => p.id === id);
          return permission?.permission_slug || id;
        }
      );

      const rolePayload = {
        name: roleData.name.trim(),
        permissions: selectedPermissionSlugs,
      };

      const response = await roleRequests.addRole(rolePayload);
      if (response.data.status_code === "000") {
        toast.success("Role created successfully!");
        navigate("/roles");
      } else {
        toast.error(response.data.reason || "Failed to create role");
      }
    } catch (error) {
      console.error("Error creating role:", error);
      toast.error("Failed to create role. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="flex justify-center items-center min-h-screen bg-gradient-to-br via-blue-50 to-indigo-50 from-slate-50">
          <Loader />
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br via-blue-50 to-indigo-50 from-slate-50">
        <div className="px-4 py-6 mx-auto max-w-8xl sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="mb-8"
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Add New Role
                </h1>
                <p className="mt-2 text-gray-600">
                  Create a new role and assign permissions
                </p>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/roles")}
                className="flex gap-2 items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Roles
              </motion.button>
            </div>
          </motion.div>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
              {/* Permissions Selection - Show first on mobile */}
              <motion.div
                variants={itemVariants}
                className="order-1 lg:col-span-2 lg:order-2"
              >
                <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
                  <div className="flex gap-3 items-center mb-6">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <Shield className="w-6 h-6 text-red-600" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      Select Permissions
                    </h2>
                  </div>

                  {/* Search */}
                  <div className="mb-6">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Search permissions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="py-2 pr-4 pl-10 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Permissions Groups */}
                  <div className="overflow-y-auto max-h-96">
                    {Object.keys(filteredGroupedPermissions).length > 0 ? (
                      <div className="space-y-4">
                        {Object.entries(filteredGroupedPermissions).map(
                          ([groupName, groupPermissions]) => (
                            <motion.div
                              key={groupName}
                              variants={itemVariants}
                              className="overflow-hidden rounded-lg border border-gray-200"
                            >
                              {/* Group Header */}
                              <div className="flex justify-between items-center p-4 bg-gray-50 border-b border-gray-200">
                                <motion.button
                                  type="button"
                                  onClick={() => toggleGroup(groupName)}
                                  className="flex flex-1 gap-3 items-center text-left"
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                >
                                  <motion.div
                                    animate={{
                                      rotate: expandedGroups.has(groupName)
                                        ? 90
                                        : 0,
                                    }}
                                    transition={{ duration: 0.2 }}
                                  >
                                    <ChevronRight className="w-5 h-5 text-gray-600" />
                                  </motion.div>
                                  <div>
                                    <h3 className="font-semibold text-gray-900">
                                      {groupName}
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                      {groupPermissions.length} permission
                                      {groupPermissions.length === 1 ? "" : "s"}
                                    </p>
                                  </div>
                                </motion.button>

                                <div className="flex gap-2">
                                  <motion.button
                                    type="button"
                                    onClick={() => selectAllInGroup(groupName)}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="px-3 py-1 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200"
                                  >
                                    Select All
                                  </motion.button>
                                  <motion.button
                                    type="button"
                                    onClick={() => clearAllInGroup(groupName)}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="px-3 py-1 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200"
                                  >
                                    Clear All
                                  </motion.button>
                                </div>
                              </div>

                              {/* Group Permissions */}
                              <AnimatePresence>
                                {expandedGroups.has(groupName) && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className="p-4 space-y-3"
                                  >
                                    {groupPermissions.map((permission) => (
                                      <motion.div
                                        key={permission.id}
                                        whileHover={{ scale: 1.01 }}
                                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                                          selectedPermissions.has(permission.id)
                                            ? "border-blue-500 bg-blue-50"
                                            : "border-gray-200 hover:border-gray-300"
                                        }`}
                                        onClick={() =>
                                          togglePermission(permission)
                                        }
                                      >
                                        <div className="flex justify-between items-center">
                                          <div className="flex gap-3 items-center">
                                            <div
                                              className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                                selectedPermissions.has(
                                                  permission.id
                                                )
                                                  ? "bg-blue-500 border-blue-500"
                                                  : "border-gray-300"
                                              }`}
                                            >
                                              {selectedPermissions.has(
                                                permission.id
                                              ) && (
                                                <Check className="w-3 h-3 text-white" />
                                              )}
                                            </div>
                                            <div>
                                              <h4 className="font-medium text-gray-900">
                                                {permission.name}
                                              </h4>
                                              <p className="text-sm text-gray-500">
                                                {permission.description}
                                              </p>
                                              {permission.dependencies &&
                                                permission.dependencies.length >
                                                  0 && (
                                                  <p className="mt-1 text-xs text-blue-600">
                                                    Requires:{" "}
                                                    {permission.dependencies.join(
                                                      ", "
                                                    )}
                                                  </p>
                                                )}
                                            </div>
                                          </div>
                                        </div>
                                      </motion.div>
                                    ))}
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </motion.div>
                          )
                        )}
                      </div>
                    ) : (
                      <div className="py-12 text-center">
                        <AlertCircle className="mx-auto w-12 h-12 text-gray-400" />
                        <h3 className="mt-4 text-lg font-medium text-gray-900">
                          No permissions found
                        </h3>
                        <p className="mt-2 text-gray-500">
                          {searchTerm
                            ? "Try adjusting your search criteria."
                            : "No permissions available."}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-4 mt-6 bg-blue-50 rounded-lg">
                    <div className="flex gap-3 items-start">
                      <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <h4 className="text-sm font-medium text-blue-900">
                          Permission Dependencies
                        </h4>
                        <p className="mt-1 text-sm text-blue-700">
                          When you select a permission, its dependencies are
                          automatically selected. For example, selecting "Manage
                          Sender IDs" will automatically select "View Sender
                          IDs".
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Role Details - Show second on mobile */}
              <motion.div
                variants={itemVariants}
                className="order-2 lg:col-span-1 lg:order-1"
              >
                <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
                  <div className="flex gap-3 items-center mb-6">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      Role Details
                    </h2>
                  </div>

                  <div className="space-y-6">
                    {/* Role Name */}
                    <div>
                      <label
                        htmlFor="roleName"
                        className="block mb-2 text-sm font-medium text-gray-700"
                      >
                        Role Name *
                      </label>
                      <input
                        type="text"
                        id="roleName"
                        value={roleData.name}
                        onChange={(e) =>
                          setRoleData({ ...roleData, name: e.target.value })
                        }
                        placeholder="Enter role name"
                        className="px-4 py-3 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>

                    {/* Selected Permissions Count */}
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <div className="flex gap-2 items-center mb-2">
                        <Shield className="w-5 h-5 text-blue-600" />
                        <span className="text-sm font-medium text-blue-900">
                          Selected Permissions
                        </span>
                      </div>
                      <p className="text-2xl font-bold text-blue-600">
                        {selectedPermissions.size}
                      </p>
                      <p className="text-sm text-blue-700">
                        {selectedPermissions.size === 0
                          ? "No permissions selected"
                          : `${selectedPermissions.size} permission${
                              selectedPermissions.size === 1 ? "" : "s"
                            } selected`}
                      </p>
                    </div>

                    {/* Submit Button */}
                    <motion.button
                      type="submit"
                      disabled={submitting || selectedPermissions.size === 0}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="flex gap-2 justify-center items-center px-6 py-3 w-full font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <>
                          <div className="w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent" />
                          Creating Role...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          Create Role
                        </>
                      )}
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddRole;
